import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import publicPages from '../../config/public-pages.json';
import seo from '../../config/seo.json';
import portalPaths from '../../config/portal-paths.json';

interface PageMetadata { title: string; description: string; prerender: boolean; }
const PAGES: Record<string, PageMetadata> = publicPages;
const PRIVATE_PATHS = new Set(portalPaths);

@Injectable({ providedIn: 'root' })
export class AmbassadorSeoService {
  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly title: Title,
    private readonly meta: Meta
  ) {}

  update(url: string): void {
    const path = url.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
    const canonicalPath = path === '/' ? '/ambassadors' : path;
    const page = PAGES[canonicalPath] ?? (PRIVATE_PATHS.has(canonicalPath) ? undefined : PAGES['/not-found']);
    // Clear marketing metadata when leaving a public page for the portal.
    this.title.setTitle(page?.title ?? 'Ambassador Portal | YachtApp');
    this.meta.updateTag({
      name: 'description',
      content: page?.description ?? 'Access your YachtApp ambassador account, referral tools, reporting, and resources.'
    });
    this.meta.updateTag({ name: 'robots', content: page?.prerender ? 'index, follow' : 'noindex, follow' });
    // Only public marketing pages advertise canonical and social URLs.
    // Query strings, fragments, portal URLs and unknown routes must not leak into them.
    this.document.querySelectorAll('link[rel="canonical"]').forEach(link => link.remove());
    const canonical = `${seo.origin}${canonicalPath}`;
    const social: Record<string, string> = {
      'og:title': page?.title ?? '',
      'og:description': page?.description ?? '',
      'og:type': 'website',
      'og:site_name': seo.siteName,
      'og:url': canonical,
      'og:image': `${seo.origin}${seo.imagePath}`,
      'og:image:secure_url': `${seo.origin}${seo.imagePath}`,
      'og:image:type': 'image/png',
      'og:image:width': String(seo.imageWidth),
      'og:image:height': String(seo.imageHeight),
      'og:image:alt': seo.imageAlt
    };
    const twitter: Record<string, string> = {
      'twitter:card': 'summary_large_image',
      'twitter:title': page?.title ?? '',
      'twitter:description': page?.description ?? '',
      'twitter:image': `${seo.origin}${seo.imagePath}`,
      'twitter:image:alt': seo.imageAlt
    };
    if (page?.prerender) {
      const link = this.document.createElement('link');
      link.rel = 'canonical';
      link.href = canonical;
      this.document.head.appendChild(link);
    }
    for (const [property, content] of Object.entries(social)) {
      if (page?.prerender) this.meta.updateTag({ property, content });
      else this.meta.removeTag(`property="${property}"`);
    }
    for (const [name, content] of Object.entries(twitter)) {
      if (page?.prerender) this.meta.updateTag({ name, content });
      else this.meta.removeTag(`name="${name}"`);
    }
  }
}
